import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { 
  Monitor, 
  Users, 
  BarChart3, 
  Calendar, 
  TrendingUp,
  ArrowRight,
  ExternalLink,
  Rocket
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useRef } from "react";

const ResultCard = ({ item, index }: { item: any; index: number }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useSpring(useTransform(mouseY, [-0.5, 0.5], [10, -10]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(mouseX, [-0.5, 0.5], [-10, 10]), { stiffness: 300, damping: 30 });

  function onMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(x);
    mouseY.set(y);
  }

  function onMouseLeave() {
    mouseX.set(0);
    mouseY.set(0);
  }

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      className="group p-8 rounded-3xl bg-[#1A1A1A]/40 border border-white/5 hover:border-white/10 transition-colors duration-500 relative overflow-hidden backdrop-blur-sm flex flex-col items-start text-left h-full"
    >
      {/* Decorative gradient spot */}
      <div className="absolute -inset-px bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      
      <div className="relative z-10 flex flex-col h-full w-full" style={{ transform: "translateZ(30px)" }}>
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-10 transition-all duration-500 border bg-white/5 border-white/10 group-hover:border-primary/30`}>
          <item.icon className={`w-6 h-6 ${item.iconColor || "text-primary"}`} />
        </div>
        
        <div className="flex items-baseline gap-1 mb-2">
            <span className={`text-5xl md:text-6xl font-black tracking-tighter ${item.numberColor || "text-primary"}`}>
                {item.number}
            </span>
        </div>

        <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-6 opacity-90">
          {item.title}
        </h4>
        
        <p className="text-white/40 text-sm leading-relaxed font-medium">
          {item.description}
        </p>

        {/* Indicator line at the bottom */}
        <div className={`mt-auto pt-8`}>
            <div className={`h-[3px] w-12 rounded-full ${item.lineColor || "bg-primary"} opacity-60 group-hover:w-full transition-all duration-700`} />
        </div>
      </div>
    </motion.div>
  );
};

const ResultsSection = () => {
  const results = [
    {
      icon: Monitor,
      number: "+250",
      title: "Sites desenvolvidos",
      description: "Projetos criados para empresas de diversos segmentos, sempre com foco em experiência e conversão.",
      numberColor: "text-[#ffbc00]",
      lineColor: "bg-[#ffbc00]",
      iconColor: "text-[#ffbc00]"
    },
    {
      icon: Users,
      number: "+1.000",
      title: "Visitantes impactados",
      description: "Milhares de usuários navegando diariamente em plataformas desenvolvidas pela Monk Studio.",
      numberColor: "text-orange-500",
      lineColor: "bg-orange-500",
      iconColor: "text-orange-500"
    },
    {
      icon: BarChart3,
      number: "+200",
      title: "Empresas atendidas",
      description: "Negócios que confiaram em nossa equipe para fortalecer sua presença digital.",
      numberColor: "text-rose-500",
      lineColor: "bg-rose-500",
      iconColor: "text-rose-500"
    },
    {
      icon: Calendar,
      number: "15 Anos",
      title: "De experiência",
      description: "Conhecimento acumulado em marketing digital, desenvolvimento web, SEO e geração de leads.",
      numberColor: "text-purple-500",
      lineColor: "bg-purple-500",
      iconColor: "text-purple-500"
    },
  ];

  return (
    <section className="py-24 bg-[#0A0A0A] relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-[40%] h-[60%] bg-primary/5 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[40%] h-[60%] bg-rose-500/5 blur-[120px] rounded-full pointer-events-none" />
      
      {/* Dot Pattern Overlay */}
      <div className="absolute inset-0 bg-[radial-gradient(#ffffff05_1px,transparent_1px)] [background-size:32px_32px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center max-w-4xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8"
          >
            <TrendingUp className="w-4 h-4 text-primary" />
            <span className="text-[10px] md:text-xs font-bold text-white tracking-[0.2em] uppercase">Resultados Reais</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-7xl font-bold text-white mb-8 tracking-tight leading-[1.2] md:leading-[1]"
          >
            Foco em Criação de Landing Pages e <br className="hidden md:block" />
            Estratégias de <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#ffbc00] to-[#ff5f80]">Marketing Digital</span>
          </motion.h2>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white/60 text-lg md:text-xl leading-relaxed max-w-2xl mx-auto font-medium"
          >
            Mais do que criar páginas bonitas, desenvolvemos projetos focados em <span className="text-white">performance</span>, <span className="text-[#ffbc00]">conversão</span> e <span className="text-[#ff5f80]">crescimento</span> para empresas de diversos segmentos.
          </motion.p>
        </div>

        {/* Results Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-7xl mx-auto">
          {results.map((item, index) => (
            <ResultCard key={index} item={item} index={index} />
          ))}
        </div>

        {/* Experience Banner (Pivot Block) */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="relative group max-w-7xl mx-auto"
        >
          {/* Subtle Outer Glow */}
          <div className="absolute -inset-px bg-gradient-to-r from-[#ffbc00]/20 to-[#ff5f80]/20 rounded-[2.5rem] blur-xl opacity-0 group-hover:opacity-100 transition duration-1000" />
          
          <div className="relative bg-[#111111]/80 backdrop-blur-md rounded-[2.5rem] border border-white/10 p-8 md:p-14 overflow-hidden">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12 relative z-10">
              
              <div className="flex flex-col md:flex-row items-center gap-10">
                {/* Animated Rocket Icon */}
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full animate-pulse" />
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-white/10 flex items-center justify-center relative z-10">
                    <Rocket className="w-10 h-10 text-primary" />
                    {/* Floating sparks around rocket could be added with absolute spans */}
                  </div>
                </div>

                <div className="text-center md:text-left max-w-xl">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6 leading-tight tracking-tight">
                    Experiência, tecnologia e estratégia trabalhando juntas para gerar <br className="hidden md:block" />
                    mais <span className="text-[#ffbc00]">oportunidades</span> para sua empresa.
                  </h3>
                  <p className="text-white/40 text-base md:text-lg font-medium leading-relaxed">
                    Cada projeto é desenvolvido com foco em velocidade, usabilidade, SEO e conversão para transformar visitantes em potenciais clientes.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4 w-full md:w-auto">
                <Button asChild size="lg" className="bg-gradient-to-r from-[#ffbc00] to-[#ff5f80] hover:scale-105 text-black font-extrabold px-10 py-7 text-base rounded-2xl shadow-xl shadow-[#ffbc00]/10 transition-all group border-none w-full md:w-64">
                  <a href="https://wa.me/message/LCYHLJAFNHCLC1" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-3">
                    Solicitar Orçamento <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
                
                <Button variant="outline" asChild size="lg" className="border-white/10 hover:bg-white/5 text-white font-bold px-10 py-7 text-base rounded-2xl transition-all w-full md:w-64 bg-transparent">
                  <a
                    href="/landingpage#portfolio"
                    onClick={(e) => {
                      const el = document.getElementById("portfolio");
                      if (el) {
                        e.preventDefault();
                        el.scrollIntoView({ behavior: "smooth" });
                      }
                    }}
                    className="flex items-center justify-center gap-3"
                  >
                    Ver Projetos <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-[-20%] right-[-10%] w-1/2 h-[150%] bg-gradient-to-br from-[#ffbc00]/5 to-transparent blur-[80px] pointer-events-none transform -rotate-12" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResultsSection;